import React from "react";
import { Box, Typography, keyframes } from "@mui/material";
import { SPECS, specsFadeSlide, specsFloat } from "./specsTheme";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(18px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const TaglineSlide = ({ lines }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      gap: "clamp(2px, 0.8vh, 10px)",
      minWidth: 0,
      maxWidth: "100%",
      containerType: "inline-size",
      animation: `${fadeIn} 0.7s ease both`,
    }}
  >
    {lines.map((line, i) => (
      <Typography
        key={`${line}-${i}`}
        sx={{
          fontFamily: SPECS.fontDisplay,
          fontWeight: 800,
          lineHeight: 0.95,
          letterSpacing: "0.03em",
          textTransform: "uppercase",
          fontSize: "clamp(1.15rem, min(7.2cqi, 8vh), 5.6rem)",
          color: "transparent",
          background: SPECS.textGradient,
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          filter: "drop-shadow(0 4px 24px rgba(0,0,0,0.55))",
          textShadow: "none",
          overflowWrap: "anywhere",
          animation: `${specsFadeSlide} 0.65s ease both`,
          animationDelay: `${i * 0.12}s`,
        }}
      >
        {line}
      </Typography>
    ))}
  </Box>
);

const GamesSlide = ({ title, games }) => (
  <Box sx={{ animation: `${fadeIn} 0.7s ease both`, width: "100%", minWidth: 0, containerType: "inline-size" }}>
    <Typography
      sx={{
        fontFamily: SPECS.fontDisplay,
        fontWeight: 800,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        fontSize: "clamp(0.95rem, min(5.5cqi, 4.2vh), 2.5rem)",
        mb: "clamp(8px, 1.6vh, 20px)",
        background: SPECS.textGradient,
        backgroundClip: "text",
        WebkitBackgroundClip: "text",
        color: "transparent",
        overflowWrap: "anywhere",
      }}
    >
      {title}
    </Typography>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: "clamp(6px, 1.2vw, 16px)",
        "@media (min-width: 900px)": {
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
        },
        alignItems: "start",
        minWidth: 0,
      }}
    >
      {games.map((game, gi) => (
        <Box
          key={game.name}
          sx={{
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "clamp(4px, 0.8vh, 8px)",
            containerType: "inline-size",
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "clamp(68px, 11vh, 110px)",
              "@media (min-width: 900px)": {
                height: "clamp(96px, 18vh, 200px)",
              },
              borderRadius: "clamp(10px, 1.5vw, 16px)",
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.14)",
              background: "rgba(0,0,0,0.35)",
              boxShadow: "0 0 24px rgba(255,45,138,0.22)",
              animation: `${specsFloat} 4s ease-in-out infinite`,
              animationDelay: `${gi * 0.35}s`,
            }}
          >
            <Box
              component="img"
              src={game.image}
              alt={game.name}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                objectPosition: "center",
                display: "block",
              }}
            />
          </Box>
          <Typography
            sx={{
              fontFamily: SPECS.fontDisplay,
              fontWeight: 700,
              fontSize: "clamp(0.5rem, 9cqi, 0.8rem)",
              letterSpacing: "0.03em",
              textTransform: "uppercase",
              textAlign: "center",
              color: SPECS.muted,
              lineHeight: 1.15,
              maxWidth: "100%",
              overflowWrap: "anywhere",
            }}
          >
            {game.name}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

const HeroSlide = ({ slide }) => {
  if (!slide) return null;

  const showSideVisual = slide.kind === "tagline" && slide.visual;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        height: "100%",
        overflow: "hidden",
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr)",
        alignItems: "center",
        gap: "clamp(8px, 2vw, 28px)",
        width: "100%",
        "@media (min-width: 1100px) and (min-height: 680px)": {
          gridTemplateColumns: showSideVisual ? "minmax(0, 1.15fr) minmax(0, 0.85fr)" : "minmax(0, 1fr)",
        },
      }}
    >
      <Box sx={{ minWidth: 0, minHeight: 0, overflow: "hidden" }}>
        {slide.kind === "games" ? (
          <GamesSlide title={slide.title} games={slide.games} />
        ) : (
          <TaglineSlide lines={slide.lines} />
        )}
      </Box>

      {showSideVisual && (
        <Box
          sx={{
            display: "none",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            height: "100%",
            minHeight: 0,
            minWidth: 0,
            overflow: "hidden",
            "@media (min-width: 1100px) and (min-height: 680px)": {
              display: "flex",
            },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: "70%",
              aspectRatio: "1",
              maxHeight: "100%",
              borderRadius: "50%",
              background: `radial-gradient(circle, rgba(255,45,138,0.3) 0%, rgba(255,193,7,0.12) 42%, transparent 70%)`,
              filter: "blur(8px)",
              animation: `${specsFloat} 5s ease-in-out infinite`,
            }}
          />
          <Box
            component="img"
            src={slide.visual}
            alt=""
            sx={{
              position: "relative",
              maxHeight: "min(42vh, 100%)",
              maxWidth: "100%",
              width: "auto",
              objectFit: "contain",
              filter: "drop-shadow(0 12px 40px rgba(0,0,0,0.55))",
              animation: `${specsFloat} 5.5s ease-in-out infinite`,
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default HeroSlide;
